import {
  ChangeDetectorRef,
  DestroyRef,
  inject,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  tap,
} from 'rxjs';

import type { Maybe } from '@plugins/shared/types';

import { TranslateService } from '../services/translate.service';
import type {
  NonGenericTranslationParams,
  TranslationParams,
} from '../services/types';

@Pipe({
  name: 'translate',
  // eslint-disable-next-line @angular-eslint/no-pipe-impure
  pure: false,
})
export class TranslatePipe implements PipeTransform {
  protected injector = inject(DestroyRef);
  protected translateService = inject(TranslateService);
  protected cdr = inject(ChangeDetectorRef);

  protected cachedValue: Maybe<string>;
  protected key = new BehaviorSubject<Maybe<string>>(undefined);
  protected params = new BehaviorSubject<Maybe<NonGenericTranslationParams>>(
    undefined,
  );

  constructor() {
    combineLatest({
      translationsChanges: merge(
        this.translateService.translations$,
        toObservable(this.translateService.externalOverrides),
      ),
      key: this.key.pipe(distinctUntilChanged()),
      params: this.params.pipe(
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
      ),
      language: this.translateService.language$.pipe(distinctUntilChanged()),
      languageLoaded: this.translateService.languageLoaded$,
    })
      .pipe(
        filter(({ languageLoaded }) => languageLoaded),
        map(({ key, params }) => {
          if (!key) return '';
          return this.translateService.instant(key, params);
        }),
        tap((result) => {
          if (result === this.cachedValue) return;
          this.cachedValue = result;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.injector),
      )
      .subscribe();
  }

  /** An empty or nullish query will result in an empty string being returned.
   * All other queries will be continuously evaluated against available translations.
   * If no translation is found, a placeholder value is returned.*/
  public transform<Key extends string>(
    query: Maybe<string>,
    params?: TranslationParams<Key>,
  ): string {
    const key = query ?? '';
    this.key.next(key);
    this.params.next(params);
    return this.cachedValue ?? this.translateService.getPlaceholder(key);
  }
}
